import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "501",
  "slug": "what-is-a-savanna-tropical-grassland-biome",
  "title": "What Is a Savanna? The Tropical Grassland Biome Explained",
  "excerpt": "Savannas are vast tropical grasslands dotted with scattered trees, ruled by a rhythm of wet and dry seasons. Here is what makes this biome work.",
  "date": "2026-06-22",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1422466654108-5e533f591881?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYXZhbm5hJTIwZ3Jhc3NsYW5kfGVufDF8MHx8fDE3ODIxMTYwNDR8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A savanna is a tropical grassland scattered with isolated trees, found between rainforests and deserts. It is defined by a climate of distinct wet and dry seasons, and it is kept open — rather than turning into forest — by fire and grazing animals.",
  "content": [
    {
      "type": "paragraph",
      "text": "Picture the African plains: a sea of golden grass stretching to the horizon, broken here and there by a flat-topped acacia or a lone baobab, with herds moving in the distance. That is the savanna, one of the planet’s great biomes — and far more than a backdrop for wildlife documentaries. It is a finely balanced landscape held together by climate, fire, and the animals that roam it."
    },
    {
      "type": "heading",
      "text": "What Is a Savanna?"
    },
    {
      "type": "paragraph",
      "text": "A savanna is a tropical or subtropical grassland with scattered trees and shrubs, sitting in the transition zone between dense rainforest and dry desert. The defining feature is its mix: enough grass to dominate the ground, but trees spread thinly enough that their canopies do not close into a forest. Sunlight reaches the ground almost everywhere, which is why grasses, not trees, rule."
    },
    {
      "type": "heading",
      "text": "A Climate of Two Seasons"
    },
    {
      "type": "paragraph",
      "text": "More than anything, the savanna is shaped by rainfall that comes in a strong annual rhythm. Instead of the four temperate seasons, the savanna has two: a wet season when rain pours down and the grasses surge green, and a long dry season when the rain all but stops and the landscape fades to gold and brown. This wet-and-dry pattern is the savanna’s signature, and it falls into the tropical category of the Köppen climate system."
    },
    {
      "type": "paragraph",
      "text": "During the dry months, surface water vanishes, grasses cure into tinder, and many trees drop their leaves to conserve moisture. When the rains return, the whole landscape transforms within weeks, triggering the great migrations of grazing animals that follow the fresh growth."
    },
    {
      "type": "heading",
      "text": "Why Trees Stay Scattered"
    },
    {
      "type": "paragraph",
      "text": "A reasonable question is why the savanna does not simply become forest where there is enough rain, or desert where there is too little. The answer is that two forces keep it open. The first is fire: in the dry season, lightning and, for thousands of years, people have set the cured grass alight. Frequent fires kill young tree seedlings but barely touch the deep-rooted grasses, which regrow quickly. The second is grazing and browsing: vast numbers of herbivores crop the vegetation and damage saplings, holding back the spread of woodland."
    },
    {
      "type": "list",
      "items": [
        "Seasonal rainfall: a pronounced wet season and dry season, rather than year-round rain.",
        "Grass dominance: continuous grass cover with widely spaced trees and shrubs.",
        "Fire: regular dry-season fires that suppress trees and recycle nutrients.",
        "Grazers and browsers: large herbivores that keep woody plants in check.",
        "Drought-adapted trees: species like acacia and baobab built to survive long dry spells."
      ]
    },
    {
      "type": "heading",
      "text": "Savannas Around the World"
    },
    {
      "type": "paragraph",
      "text": "Although the African savanna is the most famous, the biome circles the tropics. The vast grasslands of East Africa, including the Serengeti, are the classic example. South America has its own versions in the cerrado of Brazil and the llanos of Venezuela and Colombia. Northern Australia carries broad tropical savannas, and parts of India and Southeast Asia hold them too. Wherever the wet-and-dry tropical climate appears, some form of savanna tends to follow."
    },
    {
      "type": "heading",
      "text": "A Biome Full of Life"
    },
    {
      "type": "paragraph",
      "text": "Savannas support some of the richest concentrations of large animals on Earth. The open grass feeds enormous herds of grazers, which in turn support the predators that hunt them, while termites quietly rework the soil and recycle dead plant matter. The biome’s productivity during the wet season, and its dramatic die-back in the dry, drives the migrations and predator-prey dramas that make these landscapes so famous."
    },
    {
      "type": "heading",
      "text": "People and the Savanna"
    },
    {
      "type": "paragraph",
      "text": "Savannas are not just wildlife country; they are home and livelihood for millions of people. Their grasses have supported herders and their cattle for thousands of years, and the same fertile, seasonal land draws farmers wherever the rains are reliable enough. That makes the biome a balancing act. Overgrazing, the spread of cropland, and changes to the natural fire cycle can tip a savanna toward bare, eroded ground, while protected reserves work to keep its grasslands and great herds intact. Understanding how the savanna stays in balance is the first step to keeping it that way."
    },
    {
      "type": "heading",
      "text": "Spotting a Savanna From Above"
    },
    {
      "type": "paragraph",
      "text": "When comparing savanna images, check the season as well as the tree cover. Wet-season grass and dry-season grass can look very different in images of the same place."
    }
  ]
};
